import React, { Component } from 'react'
import { reduxForm, Field } from 'redux-form'
import { connect } from 'react-redux'
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import capitalize from 'lodash/capitalize'
import startCase from 'lodash/startCase'
import { History } from 'history'

import { updateLienholder, rejectTask } from '../taskActions'
import { InputField } from '../../../components/inputs'
import Loader from '../../../components/loader'
import {
  iso8601DateFormat,
  utcToLocalDateTimeFormat,
} from '../../../helpers/timeHelper'
import { Task, LienholderInfo } from '../types'
import { ReduxFormProps } from '../../../forms'

export type Callback<T = void> = (...args: any) => T

const autoCorrectedDatePipe = createAutoCorrectedDatePipe('mm/dd/yyyy')

export function formatLienholderInputsData(values: LienholderInfo) {
  const { payoffExpiresOn, verifiedPayoffAmount, perDiem } = values
  const correctFormat = iso8601DateFormat(payoffExpiresOn)
  return {
    ...values,
    payoffExpiresOn: correctFormat,
    verifiedPayoffAmount: Number(
      verifiedPayoffAmount.replace(/[^0-9\.]+/g, '')
    ),
    perDiem: Number(perDiem.replace(/[^0-9\.]+/g, '')),
  }
}

export interface Props {
  task: Task
  updateLienholder?: Callback<Promise<any>>
  rejectTask?: Callback<Promise<any>>
  history: History
}

export interface FormProps {
  id: string
}

export class LienholderInputs extends Component<
  ReduxFormProps<Props, FormProps>
> {
  state = {
    loading: false,
  }

  showLoading() {
    this.setState({
      loading: true,
    })
  }

  hideLoading() {
    this.setState({
      loading: false,
    })
  }

  updateLienholderTask = (values) => {
    this.showLoading()
    const { updateLienholder, task, history } = this.props
    return (
      updateLienholder &&
      updateLienholder(formatLienholderInputsData(values), task.id)
        .then(() => {
          this.hideLoading()
          history && history.goBack()
        })
        .catch(() => {
          this.hideLoading()
        })
    )
  }

  rejectOnClick = (): Promise<any> | void => {
    this.showLoading()
    const { rejectTask, task, history } = this.props
    const taskName = capitalize(startCase(task.taskType || ''))
    if (window.confirm(`Confirm ${taskName} rejection`)) {
      return (
        rejectTask &&
        rejectTask(task.id)
          .then(() => {
            this.hideLoading()
            history && history.goBack()
          })
          .catch(() => {
            this.hideLoading()
          })
      )
    }
  }

  backToIndex = () => {
    const { history } = this.props
    history && history.goBack()
  }

  labelOrLoading = (label: string) => {
    if (!this.state.loading) return label
    else
      return (
        <div className="loading-button-taks-container">
          <Loader isLoading className="loader--button-tasks-icon" />
        </div>
      )
  }

  render() {
    const { task, handleSubmit } = this.props
    const lienholder = task.verifiables[0] as LienholderInfo
    const nullEmptyString = (value: string) => (value === '' ? null : value)
    const currencyInput = createNumberMask({
      allowDecimal: true,
      prefix: '$',
      decimalLimit: 2,
    })

    return (
      <form
        className="form"
        name="LienholderInputs"
        onSubmit={handleSubmit(this.updateLienholderTask)}
      >
        <div className="task-lienholder">
          <h2 className="task-lienholder__title">Payoff verification</h2>
          <div className="task-lienholder__row">
            <Field<any> // TODO-TS
              label="Enter the payoff amount"
              name="verifiedPayoffAmount"
              component={InputField}
              aria-required="true"
              gridClass="field filter__field"
              mask={currencyInput}
            />

            <Field<any> // TODO-TS
              label="Enter the per diem amount"
              name="perDiem"
              component={InputField}
              aria-required="true"
              gridClass="field filter__field"
              mask={currencyInput}
            />

            <Field<any> // TODO-TS
              type="text"
              label="Select the payoff good through date"
              name="payoffExpiresOn"
              format={(value) => (value === '' ? null : value)}
              component={InputField}
              placeholder="mm/dd/yyyy"
              mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
              pipe={autoCorrectedDatePipe}
              normalize={nullEmptyString}
              keepCharPositions={true}
            />
            <div className="task-lienholder__verified">
              Last verified on:{' '}
              {lienholder.verifiedAt &&
                utcToLocalDateTimeFormat(lienholder.verifiedAt)}
            </div>
            <button
              type="submit"
              data-testid="updateTaskButton"
              className="button button--primary"
              disabled={this.state.loading}
            >
              {this.labelOrLoading('Approve')}
            </button>
            {task.taskType === 'UPDATE_LIENHOLDER_INFO' && (
              <button
                type="button"
                onClick={this.rejectOnClick}
                data-testid="rejectTaskButton"
                className="button button--primary"
                disabled={this.state.loading}
              >
                {this.labelOrLoading('Reject')}
              </button>
            )}
            <button
              type="button"
              onClick={this.backToIndex}
              data-testid="cancelTaskButton"
              className="button button--link-text task-lienholder__cancel"
              disabled={this.state.loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    )
  }
}

export const validate = (data: LienholderInfo) => {
  const { verifiedPayoffAmount, perDiem, payoffExpiresOn } = data
  const errors = {} as LienholderInfo

  if (!verifiedPayoffAmount) {
    errors.verifiedPayoffAmount = 'Verified payoff amount is required'
  }
  if (!perDiem) {
    errors.perDiem = 'Per diem is required'
  }
  if (!payoffExpiresOn) {
    errors.payoffExpiresOn = 'Payoff date is required'
  }
  return errors
}

const mapDispatchToProps = (dispatch) => ({
  updateLienholder: (values, task) => dispatch(updateLienholder(values, task)),
  rejectTask: (id) => dispatch(rejectTask(id)),
})

const ConnectedLienholderInputs = connect(
  null,
  mapDispatchToProps
)(LienholderInputs)

export default reduxForm<FormProps, Props>({
  form: 'lienholderInputs',
  validate,
  enableReinitialize: false,
  destroyOnUnmount: true,
})(ConnectedLienholderInputs)
